import { API_BASE_URL } from './constants';
import { getProductImageById } from '../api/productApi';

/**
 * Generate a unique ID
 * @param {number} length - Length of the ID
 * @returns {string} Unique ID
 */
export const generateId = (length = 16) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

/**
 * Generate cart ID (64 characters)
 * @returns {string} Cart ID
 */
export const generateCartId = () => {
  return generateId(64);
};

/**
 * Create URL-friendly slug from string
 * @param {string} text - Text to convert
 * @returns {string} URL-friendly slug
 */
export const createSlug = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove non-word chars
    .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Parse product slug to get ID
 * Format: product-name-123
 * @param {string} slug - Product slug
 * @returns {string} Product ID
 */
export const parseProductSlug = (slug) => {
  if (!slug) return null;
  const parts = slug.split('-');
  const id = parts[parts.length - 1];
  return !isNaN(id) ? id : null;
};

/**
 * Create product URL with slug
 * @param {object} product - Product object
 * @returns {string} Product URL
 */
export const createProductUrl = (product) => {
  if (!product) return '/shop';
  const slug = product.slug || createSlug(product.product_name);
  return `/product/${slug}-${product.id}`;
};

/**
 * Create category URL
 * @param {string} categoryName - Category name
 * @param {number} categoryId - Category ID
 * @returns {string} Category URL
 */
export const createCategoryUrl = (categoryName, categoryId) => {
  const slug = createSlug(categoryName);
  return `/category/${slug}`;
};

/**
 * Debounce function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in ms
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait = 300) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export const buildCheckoutItems = (items = []) =>
  (Array.isArray(items) ? items : []).map((item) => {
    const quantity = Number(item?.quantity ?? item?.qty ?? 1);
    const offerPrice = Number(item?.offer_price || item?.price || 0);
    const mrp = Number(item?.mrp || item?.offer_price || item?.price || 0);
    const productName = item?.product_name || item?.name || item?.title || '';
    const productImage = item?.image || item?.main_image || item?.product_image || item?.thumbnail || '';

    const checkoutItem = {
      product_pk: item?.id,
      product_name: productName,
      name: productName,
      size: item?.size,
      quantity,
      qty: quantity,
      offer_price: offerPrice,
      mrp,
      total_price: offerPrice * quantity,
    };

    if (productImage) {
      checkoutItem.image = productImage;
      checkoutItem.product_image = productImage;
      checkoutItem.thumbnail = productImage;
      checkoutItem.main_image = productImage;
    }

    return checkoutItem;
  });

export const getOrderItemImage = async (item = {}) => {
  if (!item || typeof item !== 'object') return '';

  const imageValue =
    item.image ||
    item.product_image ||
    item.thumbnail ||
    item.main_image ||
    item.image_url ||
    item.productImage ||
    item.product_image_url ||
    item.imageUrl ||
    '';

  if (imageValue && typeof imageValue === 'string') {
    const trimmedValue = imageValue.trim();
    if (!trimmedValue) return '';
    if (trimmedValue.startsWith('data:')) return trimmedValue;
    if (/^https?:\/\//i.test(trimmedValue) || trimmedValue.startsWith('//')) {
      return trimmedValue.startsWith('//') ? `https:${trimmedValue}` : trimmedValue;
    }

    try {
      return new URL(trimmedValue, API_BASE_URL).toString();
    } catch {
      return trimmedValue;
    }
  }

  const productId = item.product_pk || item.product_id || item.id;
  if (productId) {
    return getProductImageById(productId);
  }

  return '';
};

/**
 * Throttle function
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in ms
 * @returns {Function} Throttled function
 */
export const throttle = (func, limit = 300) => {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

/**
 * Format date
 * @param {string|Date} date - Date to format
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date
 */
export const formatDate = (date, options = {}) => {
  if (!date) return '';

  const defaultOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options,
  };

  try {
    return new Intl.DateTimeFormat('en-IN', defaultOptions).format(new Date(date));
  } catch {
    return '';
  }
};

/**
 * Format relative time (e.g., "2 hours ago")
 * @param {string|Date} date - Date to format
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date) => {
  if (!date) return '';

  const now = new Date();
  const past = new Date(date);
  const diffInSeconds = Math.floor((now - past) / 1000);

  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;

  return formatDate(date);
};

/**
 * Truncate text
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 100) => {
  if (!text || text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + '...';
};

/**
 * Capitalize first letter
 * @param {string} text - Text to capitalize
 * @returns {string} Capitalized text
 */
export const capitalize = (text) => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/**
 * Title case
 * @param {string} text - Text to convert
 * @returns {string} Title cased text
 */
export const titleCase = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .split(' ')
    .map((word) => capitalize(word))
    .join(' ');
};

/**
 * Get initials from name
 * @param {string} name - Full name
 * @returns {string} Initials
 */
export const getInitials = (name) => {
  if (!name) return '';
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

/**
 * Validate email
 * @param {string} email - Email to validate
 * @returns {boolean} Is valid
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate phone number (Indian format)
 * @param {string} phone - Phone number
 * @returns {boolean} Is valid
 */
export const isValidPhone = (phone) => {
  const phoneRegex = /^[6-9]\d{9}$/;
  return phoneRegex.test(phone?.replace(/\D/g, ''));
};

/**
 * Validate PIN code (Indian format)
 * @param {string} pinCode - PIN code
 * @returns {boolean} Is valid
 */
export const isValidPinCode = (pinCode) => {
  const pinRegex = /^[1-9][0-9]{5}$/;
  return pinRegex.test(pinCode);
};

/**
 * Format phone number
 * @param {string} phone - Phone number
 * @param {string} countryCode - Country code
 * @returns {string} Formatted phone
 */
export const formatPhone = (phone, countryCode = '91') => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  return `+${countryCode} ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
};

/**
 * Get random items from array
 * @param {Array} array - Source array
 * @param {number} count - Number of items
 * @returns {Array} Random items
 */
export const getRandomItems = (array, count = 4) => {
  if (!array || !array.length) return [];
  const shuffled = [...array].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
};

/**
 * Group array by key
 * @param {Array} array - Array to group
 * @param {string} key - Key to group by
 * @returns {object} Grouped object
 */
export const groupBy = (array, key) => {
  if (!array) return {};
  return array.reduce((result, item) => {
    const groupKey = item[key];
    if (!result[groupKey]) {
      result[groupKey] = [];
    }
    result[groupKey].push(item);
    return result;
  }, {});
};

/**
 * Deep clone object
 * @param {object} obj - Object to clone
 * @returns {object} Cloned object
 */
export const deepClone = (obj) => {
  try {
    return JSON.parse(JSON.stringify(obj));
  } catch {
    return obj;
  }
};

/**
 * Check if object is empty
 * @param {object} obj - Object to check
 * @returns {boolean} Is empty
 */
export const isEmpty = (obj) => {
  if (!obj) return true;
  if (Array.isArray(obj)) return obj.length === 0;
  if (typeof obj === 'object') return Object.keys(obj).length === 0;
  return false;
};

/**
 * Scroll to element
 * @param {string} elementId - Element ID
 * @param {number} offset - Offset from top
 */
export const scrollToElement = (elementId, offset = 80) => {
  const element = document.getElementById(elementId);
  if (element) {
    const top = element.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  }
};

/**
 * Scroll to top
 * @param {boolean} smooth - Use smooth scrolling
 */
export const scrollToTop = (smooth = true) => {
  window.scrollTo({
    top: 0,
    behavior: smooth ? 'smooth' : 'auto',
  });
};

/**
 * Copy text to clipboard
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} Success
 */
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    return true;
  }
};

/**
 * Get URL query params
 * @param {string} search - Search string
 * @returns {object} Query params
 */
export const getQueryParams = (search = window.location.search) => {
  const params = new URLSearchParams(search);
  const result = {};
  for (const [key, value] of params) {
    result[key] = value;
  }
  return result;
};

/**
 * Build URL with query params
 * @param {string} baseUrl - Base URL
 * @param {object} params - Query params
 * @returns {string} URL with params
 */
export const buildUrl = (baseUrl, params = {}) => {
  const url = new URL(baseUrl, window.location.origin);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, value);
    }
  });
  return url.pathname + url.search;
};

/**
 * Local storage helpers
 */
export const storage = {
  get: (key, defaultValue = null) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch {
      return defaultValue;
    }
  },
  set: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch {
      return false;
    }
  },
  remove: (key) => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch {
      return false;
    }
  },
  clear: () => {
    try {
      localStorage.clear();
      return true;
    } catch {
      return false;
    }
  },
};

/**
 * Session storage helpers
 */
export const sessionStore = {
  get: (key, defaultValue = null) => {
    try {
      const item = sessionStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch {
      return defaultValue;
    }
  },
  set: (key, value) => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch {
      return false;
    }
  },
  remove: (key) => {
    try {
      sessionStorage.removeItem(key);
      return true;
    } catch {
      return false;
    }
  },
};

/**
 * Class names utility (similar to clsx)
 * @param  {...any} classes - Class names
 * @returns {string} Combined class names
 */
export const cn = (...classes) => {
  return classes
    .flat()
    .filter((x) => typeof x === 'string' && x.trim())
    .join(' ');
};
